import { useMemo } from "react";
import {
  BUCKETS,
  BUCKET_LABELS,
  STATUSES,
  STATUS_LABELS,
  type AuditEntry,
  type Bucket,
  type Status,
} from "../shared/types.ts";
import { renderMarkdown } from "./markdown.ts";

export function StatusBadge({ status }: { status: Status }) {
  return <span className={`badge status-${status}`}>{STATUS_LABELS[status]}</span>;
}

export function BucketBadge({ bucket }: { bucket: Bucket }) {
  return <span className="badge bucket">{BUCKET_LABELS[bucket]}</span>;
}

/**
 * A row of mutually-exclusive buttons standing in for a `<select>`. Exactly
 * one option is ever "on" (filled); the rest are outlined. Implements
 * roving-tabindex radio-group keyboard semantics: arrow keys move both focus
 * and selection, matching native `role="radio"` behavior.
 */
export function SegmentedControl<T extends string>({
  legend,
  options,
  value,
  onChange,
  disabled,
}: {
  legend: string;
  options: readonly { value: T; label: string }[];
  value: T;
  onChange: (value: T) => void;
  disabled?: boolean;
}) {
  const move = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const index = options.findIndex((option) => option.value === value);
    if (index === -1) return;
    let nextIndex = -1;
    if (event.key === "ArrowRight" || event.key === "ArrowDown") {
      nextIndex = (index + 1) % options.length;
    } else if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
      nextIndex = (index - 1 + options.length) % options.length;
    } else if (event.key === "Home") {
      nextIndex = 0;
    } else if (event.key === "End") {
      nextIndex = options.length - 1;
    }
    if (nextIndex === -1) return;
    event.preventDefault();
    const next = options[nextIndex];
    if (!next) return;
    onChange(next.value);
    const container = event.currentTarget;
    (container.children[nextIndex] as HTMLElement | undefined)?.focus();
  };

  return (
    <div className="segmented" role="radiogroup" aria-label={legend} onKeyDown={move}>
      {options.map((option) => {
        const on = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={on}
            tabIndex={on ? 0 : -1}
            className={`segment ${on ? "segment-on" : ""}`}
            disabled={disabled}
            onClick={() => onChange(option.value)}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

const BUCKET_OPTIONS = BUCKETS.map((bucket) => ({ value: bucket, label: BUCKET_LABELS[bucket] }));
const STATUS_OPTIONS = STATUSES.map((status) => ({ value: status, label: STATUS_LABELS[status] }));

export function BucketPicker({
  value,
  onChange,
  disabled,
  legend = "Bucket",
}: {
  value: Bucket;
  onChange: (bucket: Bucket) => void;
  disabled?: boolean;
  legend?: string;
}) {
  return (
    <SegmentedControl legend={legend} options={BUCKET_OPTIONS} value={value} onChange={onChange} disabled={disabled} />
  );
}

export function StatusPicker({
  value,
  onChange,
  disabled,
  legend = "Status",
}: {
  value: Status;
  onChange: (status: Status) => void;
  disabled?: boolean;
  legend?: string;
}) {
  return (
    <SegmentedControl legend={legend} options={STATUS_OPTIONS} value={value} onChange={onChange} disabled={disabled} />
  );
}

export function AreaBadge({ area }: { area: string }) {
  if (!area) return null;
  return <span className="badge area">{area}</span>;
}

export function Markdown({ source }: { source: string }) {
  const html = useMemo(() => renderMarkdown(source), [source]);
  if (!source.trim()) return <p className="muted">Nothing here yet.</p>;
  return <div className="markdown" dangerouslySetInnerHTML={{ __html: html }} />;
}

export function Banner({ kind, children }: { kind: "error" | "info" | "warn"; children: React.ReactNode }) {
  return <div className={`banner banner-${kind}`}>{children}</div>;
}

export function Loading({ label = "Loading…" }: { label?: string }) {
  return <p className="muted center pad">{label}</p>;
}

export function Empty({ children }: { children: React.ReactNode }) {
  return <p className="muted center pad">{children}</p>;
}

export function formatDate(iso: string | null): string {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

/**
 * "3h ago" for anything in the last day, "Yesterday" / "4d ago" for the rest
 * of the week, and a compact absolute stamp (e.g. "Tue, Aug 11, 2:14 PM")
 * beyond that — the audit log is the one place recency actually matters.
 */
export function formatRelative(iso: string): string {
  const date = new Date(iso);
  const diffMs = Date.now() - date.getTime();
  const diffMin = Math.round(diffMs / 60_000);
  const diffHour = Math.round(diffMin / 60);
  const diffDay = Math.round(diffHour / 24);

  if (diffMin < 1) return "Just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHour < 24) return `${diffHour}h ago`;
  if (diffDay === 1) return "Yesterday";
  if (diffDay < 7) return `${diffDay}d ago`;

  return date.toLocaleString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

/** Turns one audit_log row into a compact, human-readable phrase. */
export function describeAuditEntry(entry: AuditEntry): string {
  const who = entry.person_name ?? "Someone";
  const d = entry.detail ?? {};

  const str = (value: unknown): string => (typeof value === "string" ? value : "");
  const humanStatus = (value: unknown) => {
    const raw = str(value);
    return raw ? (STATUS_LABELS[raw as Status] ?? raw).toLowerCase() : "";
  };
  const humanBucket = (value: unknown) => {
    const raw = str(value);
    return raw ? (BUCKET_LABELS[raw as Bucket] ?? raw) : "";
  };

  const field = str(d.field);
  const to = str(d.to);
  const excerpt = str(d.excerpt);
  const blockerTitle = str(d.blocked_by_title);

  switch (entry.action) {
    case "created":
      return `${who} created this project`;

    case "status_changed":
      return `${who} changed status: ${humanStatus(d.from)} → ${humanStatus(d.to)}`;

    case "bucket_changed":
      return `${who} moved this to ${humanBucket(d.to)}`;

    case "updated": {
      if (field === "title") return `${who} renamed this to "${to}"`;
      if (field === "area") return to ? `${who} set area to "${to}"` : `${who} cleared the area`;
      if (field === "notes_md") return `${who} updated the notes`;
      return `${who} updated ${field || "this project"}`;
    }

    case "photo_added":
      return `${who} added a photo`;
    case "photo_removed":
      return `${who} removed a photo`;

    case "comment_added":
      return excerpt ? `${who} commented: "${excerpt}"` : `${who} added a comment`;

    case "dependency_added":
      return blockerTitle ? `${who} added a blocker: ${blockerTitle}` : `${who} added a blocker`;
    case "dependency_removed":
      return blockerTitle ? `${who} removed a blocker: ${blockerTitle}` : `${who} removed a blocker`;

    default:
      return `${who} updated this project`;
  }
}
